// Three printable menu templates — faithful TSX port of the WordPress printer's
// templates.jsx. Each renders the same `.tpl-*` DOM the ported styles.css targets,
// so the visual output matches the original. Consumes the PrintData shapes.
import { Fragment } from 'react';
import type { PrintData, PrintSection } from '../data/printData.js';

export type MenuType = 'daily' | 'formule' | 'carte';
type Restaurant = PrintData['RESTAURANT'];

export interface TemplateOptions {
  showPrices: boolean;
  showDate: boolean;
  chefNote: string;
  showAllergenNote: boolean;
  showLogo: boolean;
  headlinePrice: string;
  extraPrice: string;
}
export interface TemplateData {
  subtitle?: string;
  date?: string;
  sections: PrintSection[];
}
export interface TemplateProps {
  type: MenuType;
  data: TemplateData;
  restaurant: Restaurant;
  options: TemplateOptions;
}

/* -------- Shared bits -------- */
function Monogram({ name }: { name: string }) {
  const letters = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return <span className="monogram">{letters}</span>;
}

function formatTypeLabel(type: MenuType, options: TemplateOptions): string {
  const price = (options.headlinePrice || '').trim();
  if (type === 'daily') return price ? `Menu du Jour ${price}` : 'Menu du Jour';
  if (type === 'formule') return price ? `Formule ${price}` : 'La Formule';
  return 'La Carte';
}

const isCentered = (type: MenuType) => type === 'daily' || type === 'formule';

/* Caption under each course title: daily none; formule "au choix"; carte none. */
const sectionCaption = (type: MenuType): string => (type === 'formule' ? 'au choix' : '');

const threePlatsLine = (options: TemplateOptions): string => {
  const x = (options.extraPrice || '').trim();
  return x ? `Entrée + Plat + Dessert ${x}` : 'Entrée + Plat + Dessert';
};

/* Manuscrit-only: "/" between courses in a combo (not "+"), two stacked lines
   instead of side-by-side pills — the pills didn't fit 3 plats on one row at
   the centered (Menu du Jour/Formule) font sizes. */
const threePlatsSlashLine = (options: TemplateOptions): string => {
  const x = (options.extraPrice || '').trim();
  return x ? `Entrée / Plat / Dessert ${x}` : 'Entrée / Plat / Dessert';
};

/**
 * Menu du Jour only: the course intitulés (« Entrée » / « Plat principal » /
 * « Dessert ») are redundant when there's one dish per course, so they're
 * dropped in favour of a small ornamental divider between dishes. Vector, not
 * a Unicode glyph — a squiggle character renders inconsistently across the
 * serif/sans/display font pairings and some don't have one at all.
 */
function CourseDivider() {
  return (
    <svg className="tpl-course-divider" viewBox="0 0 140 14" aria-hidden="true" focusable="false">
      <path d="M4,7 C14,-3 24,17 34,7 C44,-3 54,17 58,7" />
      <circle cx="70" cy="7" r="1.8" />
      <path d="M82,7 C92,-3 102,17 112,7 C122,-3 132,17 136,7" />
    </svg>
  );
}

function BrandBlock({ type, restaurant, options, variant }: { type: MenuType; restaurant: Restaurant; options: TemplateOptions; variant: string }) {
  const useLogo = options.showLogo && restaurant.logo && (type === 'daily' || type === 'formule');
  const tagline = `Le Montaigu Hôtel *** · Restaurant · Bar — ${restaurant.location}`;
  if (useLogo) {
    return <img src={restaurant.logo} alt={restaurant.name} className={'brand-logo brand-logo-' + variant} />;
  }
  return <div className={'brand-text brand-text-' + variant}>{tagline}</div>;
}

/* ============ 1. CLASSIQUE ============ */
export function ClassiqueTemplate({ type, data, restaurant, options }: TemplateProps) {
  const title = formatTypeLabel(type, options);
  const centered = isCentered(type);
  return (
    <div className={'tpl tpl-classique' + (centered ? ' tpl--centered' : '')} data-template="classique">
      <header className="tpl-c-header">
        <div className="tpl-c-rule" />
        <div className="tpl-c-brand">
          <BrandBlock type={type} restaurant={restaurant} options={options} variant="classique" />
        </div>
        <div className="tpl-c-rule" />
      </header>

      <div className="tpl-c-title-wrap">
        <div className="tpl-c-ornament">❦</div>
        <h1 className="tpl-c-title">{title}</h1>
        {type === 'daily' && options.showDate && data.date && <div className="tpl-c-date">{data.date}</div>}
        {data.subtitle && <div className="tpl-c-subtitle">{data.subtitle}</div>}
      </div>

      {(type === 'daily' || type === 'formule') && (
        <div className="tpl-c-prices">
          <div>Entrée + Plat ou Plat + Dessert</div>
          <div className="tpl-c-dotrow" />
          <div>{threePlatsLine(options)}</div>
        </div>
      )}

      <div className="tpl-c-body">
        {data.sections.map((sec, secIdx) => {
          const cap = sectionCaption(type);
          const isDaily = type === 'daily';
          return (
            <Fragment key={sec.id}>
              <section className="tpl-c-section">
                {!isDaily && (
                  <h2 className="tpl-c-section-title">
                    {sec.title}
                    {cap && <em className="tpl-c-caption"> ({cap})</em>}
                  </h2>
                )}
                <ul className="tpl-c-items">
                  {sec.items.map((it) => {
                    const hasPrice = !centered && options.showPrices && it.price;
                    return (
                      <li key={it.id} className="tpl-c-item">
                        <div className={'tpl-c-item-main' + (hasPrice ? '' : ' no-price')}>
                          <span className="tpl-c-item-name">{it.name}</span>
                          {hasPrice && <span className="tpl-c-leader" />}
                          {hasPrice && <span className="tpl-c-item-price">{it.price}</span>}
                        </div>
                        {it.desc && <div className="tpl-c-item-desc">{it.desc}</div>}
                      </li>
                    );
                  })}
                </ul>
                {sec.notes.map((n, i) => <p key={i} className="tpl-c-note">{n}</p>)}
              </section>
              {isDaily && secIdx < data.sections.length - 1 && <CourseDivider />}
            </Fragment>
          );
        })}

        {options.chefNote && <div className="tpl-c-chef">— {options.chefNote} —</div>}
      </div>

      {options.showAllergenNote && (
        <footer className="tpl-c-footer">
          <div className="tpl-c-rule" />
          <p className="tpl-c-allergen">{restaurant.note}</p>
        </footer>
      )}
    </div>
  );
}

/* ============ 2. MODERNE ============ */
export function ModerneTemplate({ type, data, restaurant, options }: TemplateProps) {
  const title = formatTypeLabel(type, options);
  const sections = data.sections;
  const mid = Math.ceil(sections.length / 2);
  const colA = sections.slice(0, mid);
  const colB = sections.slice(mid);
  const singleCol = type !== 'carte';
  const centered = isCentered(type);

  return (
    <div className={'tpl tpl-moderne' + (centered ? ' tpl--centered' : '')} data-template="moderne">
      <header className="tpl-m-header">
        <div className="tpl-m-brand">
          <BrandBlock type={type} restaurant={restaurant} options={options} variant="moderne" />
        </div>
        <div className="tpl-m-title-block">
          <div className="tpl-m-kicker">— {title} —</div>
          {type === 'daily' && options.showDate && data.date && <div className="tpl-m-date">{data.date}</div>}
        </div>
      </header>

      {(type === 'daily' || type === 'formule') && (
        <div className="tpl-m-prix">
          <span>Entrée + Plat ou Plat + Dessert</span>
          <span className="tpl-m-slash">/</span>
          <span>{threePlatsLine(options)}</span>
        </div>
      )}

      <div className={'tpl-m-body ' + (singleCol ? 'is-single' : 'is-double')}>
        {singleCol ? (
          <ModerneColumn sections={sections} options={options} type={type} />
        ) : (
          <Fragment>
            <ModerneColumn sections={colA} options={options} type={type} />
            <ModerneColumn sections={colB} options={options} type={type} />
          </Fragment>
        )}
      </div>

      {options.chefNote && <div className="tpl-m-chef">{options.chefNote}</div>}

      {options.showAllergenNote && (
        <footer className="tpl-m-footer">
          <p>{restaurant.note}</p>
        </footer>
      )}
    </div>
  );
}

function ModerneColumn({ sections, options, type }: { sections: PrintSection[]; options: TemplateOptions; type: MenuType }) {
  const centered = isCentered(type);
  const isDaily = type === 'daily';
  return (
    <div className="tpl-m-col">
      {sections.map((sec, secIdx) => {
        const cap = sectionCaption(type);
        return (
          <Fragment key={sec.id}>
            <section className="tpl-m-section">
              {!isDaily && (
                <h2 className="tpl-m-section-title">
                  <span className="tpl-m-num">·</span> {sec.title}
                  {cap && <span className="tpl-m-cap"> ({cap})</span>}
                </h2>
              )}
              <ul className="tpl-m-items">
                {sec.items.map((it) => {
                  const hasPrice = !centered && options.showPrices && it.price;
                  return (
                    <li key={it.id} className="tpl-m-item">
                      <div className="tpl-m-item-row">
                        <span className="tpl-m-item-name">{it.name}</span>
                        {hasPrice && <span className="tpl-m-item-price">{it.price}</span>}
                      </div>
                      {it.desc && <div className="tpl-m-item-desc">{it.desc}</div>}
                    </li>
                  );
                })}
              </ul>
              {sec.notes.map((n, i) => <p key={i} className="tpl-m-note">{n}</p>)}
            </section>
            {isDaily && secIdx < sections.length - 1 && <CourseDivider />}
          </Fragment>
        );
      })}
    </div>
  );
}

/* ============ 3. MANUSCRIT ============ */
export function ManuscritTemplate({ type, data, restaurant, options }: TemplateProps) {
  const title = formatTypeLabel(type, options);
  const centered = isCentered(type);
  return (
    <div className={'tpl tpl-manuscrit' + (centered ? ' tpl--centered' : '')} data-template="manuscrit">
      <div className="tpl-ms-inner">
        <header className="tpl-ms-header">
          <BrandBlock type={type} restaurant={restaurant} options={options} variant="manuscrit" />
          <h1 className="tpl-ms-title">{title}</h1>
          <div className="tpl-ms-flourish">∼</div>
          {type === 'daily' && options.showDate && data.date && <div className="tpl-ms-date">{data.date}</div>}
          {data.subtitle && <div className="tpl-ms-subtitle">{data.subtitle}</div>}
        </header>

        {(type === 'daily' || type === 'formule') && (
          <div className="tpl-ms-prices">
            <div className="tpl-ms-price-line">
              Entrée / Plat <span className="tpl-ms-price-ou">ou</span> Plat / Dessert
            </div>
            <div className="tpl-ms-price-line">{threePlatsSlashLine(options)}</div>
          </div>
        )}

        <div className="tpl-ms-body">
          {data.sections.map((sec, secIdx) => {
            const cap = sectionCaption(type);
            const isDaily = type === 'daily';
            return (
              <Fragment key={sec.id}>
                <section className="tpl-ms-section">
                  {!isDaily && (
                    <div className="tpl-ms-section-head">
                      <h2 className="tpl-ms-section-title">{sec.title}</h2>
                      {cap && <span className="tpl-ms-caption">({cap})</span>}
                      <span className="tpl-ms-head-rule" />
                    </div>
                  )}
                  <ul className="tpl-ms-items">
                    {sec.items.map((it) => {
                      const hasPrice = !centered && options.showPrices && it.price;
                      return (
                        <li key={it.id} className="tpl-ms-item">
                          <div className="tpl-ms-item-head">
                            <span className="tpl-ms-item-name">{it.name}</span>
                            {hasPrice && <span className="tpl-ms-item-price">{it.price}</span>}
                          </div>
                          {it.desc && <div className="tpl-ms-item-desc">{it.desc}</div>}
                        </li>
                      );
                    })}
                  </ul>
                  {sec.notes.map((n, i) => <p key={i} className="tpl-ms-note">{n}</p>)}
                </section>
                {isDaily && secIdx < data.sections.length - 1 && <CourseDivider />}
              </Fragment>
            );
          })}

          {options.chefNote && <div className="tpl-ms-chef">« {options.chefNote} »</div>}
        </div>

        {options.showAllergenNote && (
          <footer className="tpl-ms-footer">
            <p>{restaurant.note}</p>
          </footer>
        )}
      </div>
    </div>
  );
}

export const TEMPLATES = {
  classique: ClassiqueTemplate,
  moderne: ModerneTemplate,
  manuscrit: ManuscritTemplate,
} as const;
export type TemplateKey = keyof typeof TEMPLATES;
